import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, Trash2, KeyRound, HeartHandshake } from "lucide-react";

const empty = { name: "", email: "", password: "", role: "user" };

export default function UsersTab({ users, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [anffasOpen, setAnffasOpen] = useState(false);
  const [anffasForm, setAnffasForm] = useState(empty);

  const createUser = async () => {
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Utente creato");
      setOpen(false);
      setForm(empty);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const createAnffasUser = async () => {
    setSaving(true);
    try {
      const { data: apps } = await api.get("/apps");
      const area = (apps || []).find((a) => a.route === "/area-riservata");
      if (!area) { toast.error("App 'Area Riservata' non trovata"); return; }
      await api.post("/users", { ...anffasForm, role: "user", permissions: [area.app_id] });
      toast.success("Utente ANFFAS creato · accesso solo al Servizio Trasporto");
      setAnffasOpen(false);
      setAnffasForm(empty);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.user_id}`, { is_active: !u.is_active });
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/users/${u.user_id}`, { role });
      toast.success("Ruolo aggiornato");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeUser = async (u) => {
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success("Utente eliminato");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const resetPassword = async () => {
    try {
      await api.put(`/users/${pwUser.user_id}`, { password: newPw });
      toast.success("Password aggiornata");
      setPwUser(null);
      setNewPw("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
      <div className="flex items-center justify-between p-4 border-b border-slate-200">
        <div>
          <h3 className="font-heading text-lg text-slate-900">Gestione utenti</h3>
          <p className="text-sm text-slate-500">{users.length} account registrati</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="add-anffas-user-btn" variant="outline" onClick={() => setAnffasOpen(true)}
            className="border-violet-300 text-violet-700 hover:bg-violet-50">
            <HeartHandshake className="h-4 w-4 mr-2" /> Utente ANFFAS (solo trasporto)
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="brand-gradient text-white border-0">
              <UserPlus className="h-4 w-4 mr-2" /> Nuovo utente
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Crea nuovo utente</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Ruolo</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Utente</SelectItem>
                    <SelectItem value="admin">Amministratore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-user-btn" onClick={createUser} disabled={saving || !form.email || !form.password} className="brand-gradient text-white border-0">
                {saving ? "Salvataggio…" : "Crea utente"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Quick create: utente ANFFAS (solo trasporto) */}
      <Dialog open={anffasOpen} onOpenChange={setAnffasOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <HeartHandshake className="h-5 w-5 text-violet-600" /> Nuovo utente ANFFAS
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-lg bg-violet-50 border border-violet-200 px-3 py-2 text-xs text-violet-800">
              L'utente avrà accesso <b>solo</b> all'app <b>Gestione Servizi Trasporto (Area Riservata)</b> come servizio Benefit ETS, senza vedere gli altri moduli della Suite.
            </div>
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input data-testid="anffas-name-input" value={anffasForm.name} onChange={(e) => setAnffasForm({ ...anffasForm, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input data-testid="anffas-email-input" type="email" value={anffasForm.email} onChange={(e) => setAnffasForm({ ...anffasForm, email: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input data-testid="anffas-password-input" type="text" value={anffasForm.password} onChange={(e) => setAnffasForm({ ...anffasForm, password: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-anffas-user-btn" onClick={createAnffasUser} disabled={saving || !anffasForm.email || !anffasForm.password}
              className="bg-violet-600 hover:bg-violet-700 text-white border-0">
              {saving ? "Salvataggio…" : "Crea utente ANFFAS"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Utente</TableHead>
              <TableHead>Ruolo</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Attivo</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                <TableCell>
                  <div className="font-medium text-slate-900">{u.name}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                    <SelectTrigger className="w-40 h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Utente</SelectItem>
                      <SelectItem value="admin">Amministratore</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize">{u.auth_provider}</Badge>
                </TableCell>
                <TableCell>
                  <Switch data-testid={`user-active-${u.user_id}`} checked={u.is_active} onCheckedChange={() => toggleActive(u)} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {u.auth_provider === "password" && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-900" onClick={() => setPwUser(u)} data-testid={`reset-pw-${u.user_id}`}>
                        <KeyRound className="h-4 w-4" />
                      </Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-user-${u.user_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminare {u.name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            L'utente perderà l'accesso al portale. Azione irreversibile.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annulla</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeUser(u)} className="bg-red-600 hover:bg-red-700 text-white">Elimina</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Reimposta password</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Nuova password per {pwUser?.email}</Label>
            <Input data-testid="new-password-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
          </div>
          <DialogFooter>
            <Button data-testid="save-password-btn" onClick={resetPassword} disabled={!newPw} className="brand-gradient text-white border-0">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
